import { Link } from 'react-router'
import type { Conversation } from '@/types/message'

interface ConversationItemProps {
  conversation: Conversation
  currentUserId: string
}

export function ConversationItem({ conversation, currentUserId }: ConversationItemProps) {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' })
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    }
  }

  // Get display name for conversation
  const getDisplayName = () => {
    if (conversation.name) {
      return conversation.name
    }

    if (conversation.type === 'direct') {
      // For direct chats, show the other person's name
      const otherParticipant = conversation.participants.find(
        (p) => p.userId !== currentUserId
      )
      return otherParticipant?.name || 'Unknown'
    }

    if (conversation.type === 'broadcast') {
      return 'Broadcast'
    }

    // For group chats, list participant names
    const otherNames = conversation.participants
      .filter((p) => p.userId !== currentUserId)
      .map((p) => p.name)
      .slice(0, 3)

    if (otherNames.length > 3) {
      return `${otherNames.join(', ')} +${conversation.participants.length - 4}`
    }
    return otherNames.join(', ') || 'Group'
  }

  // Get icon for conversation type
  const getIcon = () => {
    switch (conversation.type) {
      case 'broadcast':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
            />
          </svg>
        )
      case 'group':
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        )
      default:
        return (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        )
    }
  }

  return (
    <Link
      to={`/messages/${conversation.id}`}
      className="flex items-center gap-3 p-4 hover:bg-gray-50 border-b border-gray-100"
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900 truncate ph-no-capture">{getDisplayName()}</h3>
          {conversation.lastMessage && (
            <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
              {formatTime(conversation.lastMessage.createdAt)}
            </span>
          )}
        </div>

        {conversation.lastMessage && (
          <p className="text-sm text-gray-500 truncate mt-0.5 ph-no-capture">
            {conversation.lastMessage.senderId === currentUserId ? 'You: ' : ''}
            {conversation.lastMessage.content}
          </p>
        )}

        {!conversation.allowReplies && conversation.type === 'broadcast' && (
          <span className="inline-block text-xs text-gray-400 mt-1">Announcement</span>
        )}
      </div>

      {conversation.unreadCount > 0 && (
        <span className="flex-shrink-0 bg-primary-600 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">
          {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
        </span>
      )}
    </Link>
  )
}
